import { Link, useParams } from "@tanstack/react-router";
import { cn } from "@/lib/cn";
import { useFilters } from "@/hooks/useFilters";
import type { TabId } from "@/api/types";
import { ScopeSegmented } from "@/components/filters/ScopeSegmented";

const TABS: { id: TabId; label: string }[] = [
  { id: "global", label: "Global" },
  { id: "teams", label: "Equipos" },
  { id: "streaks", label: "Rachas" },
  { id: "movers", label: "Movimientos" },
  { id: "efficiency", label: "Eficiencia" },
  { id: "burn", label: "Quema de plan" },
  { id: "achievements", label: "Logros" },
  { id: "pulse", label: "Pulso" },
];

/** Single-row strip: 8 tab links on the left, ScopeSegmented on the
 * right. flex with justify-between and nowrap — never wraps at 1280px. */
export function TabsNav() {
  const params = useParams({ strict: false }) as { tabId?: string };
  const activeTabId = (params.tabId ?? "global") as TabId;
  const { filters, setFilter } = useFilters();

  // Teams tab is only meaningful when scope=all. Greys out otherwise.
  const teamsDisabled = filters.scope !== "all";

  return (
    <nav
      aria-label="Secciones"
      className="mb-2 flex flex-nowrap items-center justify-between gap-6"
    >
      <div role="tablist" className="flex flex-nowrap items-center gap-1">
        {TABS.map((tab) => {
          const active = tab.id === activeTabId;
          const disabled = tab.id === "teams" && teamsDisabled;
          return (
            <Link
              key={tab.id}
              to="/tabs/$tabId"
              params={{ tabId: tab.id }}
              search={(prev) => prev}
              role="tab"
              aria-selected={active}
              aria-disabled={disabled}
              className={cn(
                "whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-all",
                active
                  ? "relative z-10 bg-white/10 text-text-1 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.3),0_0_0_10px_rgba(255,255,255,0.1),0_0_40px_rgba(255,255,255,0.4),0_0_80px_rgba(167,139,250,0.55),0_0_140px_rgba(236,72,153,0.3)]"
                  : "text-text-3 hover:bg-white/5 hover:text-text-1",
                disabled && "pointer-events-none opacity-40",
              )}
            >
              {tab.label}
            </Link>
          );
        })}
      </div>

      <div className="flex-shrink-0">
        <ScopeSegmented
          value={filters.scope}
          onChange={(v) => setFilter("scope", v)}
        />
      </div>
    </nav>
  );
}
